import React, { FC, useMemo } from 'react'

import { useI18n } from '@affiliate-cabinet/i18n'
import { Select, SelectPropertiesInterface } from '@affstore-ds/uikit'

import { useAdvertiser } from '../../entities/advertiser'
import { InputLoader } from '../../shared/components/InputLoader'
import { commonTranslationKeys } from '../../shared/translations/common.translationKeys'
import { selectTranslationKeys } from '../../shared/translations/select.translationKeys'
import { promoBannerLandingsPageLinksSelectTranslationKeys } from './translations/promoBannerLandingsPageLinksSelect.translationKeys'

import { usePromoBannersByIdWithLandings } from '../../entities/hooks/usePromoBannersByIdWithLandings'

export const PromoBannerLandingsPageLinksSelect: FC<
  Omit<SelectPropertiesInterface, 'data'> & { advertiserId: number; bannerId: number; locale: string }
> = ({ label, advertiserId, bannerId, disabled, locale, ...rest }) => {
  const { i18n } = useI18n()
  const { advertiser, loading: asvertiserIsFetching } = useAdvertiser(advertiserId)
  const { landings, landingsIsFetching } = usePromoBannersByIdWithLandings(bannerId)

  const PREPARED_LANDINGS_LINKS = useMemo(() => {
    const advertiserLinks = (advertiser?.links || []).map((link) => ({
      'data-testid': `link-item-${link.uid}`,
      label: i18n.t(link.label),
      value: link.url,
    }))

    const landingLinks = landings.map((landing) => ({
      'data-testid': `link-item-${landing.links[locale]?.linkId || landing.links['en_US']?.linkId}`,
      label: landing.titles[locale] || landing.titles['en_US'],
      value: landing.links[locale]?.link || landing.links['en_US']?.link,
    }))

    return [...advertiserLinks, ...landingLinks]
  }, [advertiser?.links, i18n, landings, locale])

  return (
    <Select
      data-testid="landing-links-select"
      middlewares={{ flip: false, shift: false }}
      label={label || i18n.t(promoBannerLandingsPageLinksSelectTranslationKeys.promoLandingLabel)}
      bottomSheetTitle={label || i18n.t(promoBannerLandingsPageLinksSelectTranslationKeys.promoLandingLabel)}
      clearable={false}
      data={PREPARED_LANDINGS_LINKS || []}
      disabled={disabled || asvertiserIsFetching || landingsIsFetching}
      maxDropdownHeight="calc(100vh / 2.5)"
      nothingFoundMessage={i18n.t(commonTranslationKeys.commonNoData)}
      placeholder={i18n.t(selectTranslationKeys.selectPlaceholder)}
      position="bottom"
      rightSection={(asvertiserIsFetching || landingsIsFetching) && <InputLoader />}
      variant="responsive"
      withScrollArea={true}
      {...rest}
    />
  )
}
